/**
 * The console is served by the same nginx vhost that proxies `/api/`, so the
 * bridge is always this origin — there is no endpoint to configure and every
 * request is same-origin. Only the bearer token has to be supplied.
 */

const TOKEN_KEY = 'pm2:token';

export type TokenScope = 'session' | 'device';

/**
 * Where the token is kept.
 *
 * `session` (the default) holds it in `sessionStorage`, so closing the tab
 * forgets it. `device` promotes it to `localStorage` on an explicit opt-in:
 * the token grants read access to every process on the host, and persisting it
 * should be a decision rather than a side effect of signing in.
 */
export function readToken(): { token: string; scope: TokenScope } | null {
  try {
    const persisted = localStorage.getItem(TOKEN_KEY);
    if (persisted) return { token: persisted, scope: 'device' };
    const ephemeral = sessionStorage.getItem(TOKEN_KEY);
    if (ephemeral) return { token: ephemeral, scope: 'session' };
  } catch {
    // Storage can be blocked outright (private mode, strict site settings);
    // the console still works, it just asks for the token every load.
  }
  return null;
}

export function writeToken(token: string, scope: TokenScope): void {
  try {
    if (scope === 'device') {
      localStorage.setItem(TOKEN_KEY, token);
      sessionStorage.removeItem(TOKEN_KEY);
    } else {
      sessionStorage.setItem(TOKEN_KEY, token);
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // Non-fatal: the in-memory token still drives this page load.
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Nothing to clean up if storage was never available.
  }
}

/** `wss://host` for the stream, derived from however this page was loaded. */
export function streamOrigin(): string {
  return window.location.origin.replace(/^http/i, 'ws');
}
